// A tiny IndexedDB-backed cache for report data, so switching filters
// (groupBy/basis, date ranges, etc.) doesn't re-fetch from the backend
// every time — each cache entry expires on its own after TTL_MS, so
// stale data never lingers silently past its shelf life.
const DB_NAME = "msp_reports_cache";
const STORE_NAME = "reports";
const DB_VERSION = 1;
const TTL_MS = 30 * 60 * 1000; // 30 minutes

let dbPromise = null;

const openDb = () => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "key" });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
};

// Builds a stable cache key from a report name + its param object — same
// params (regardless of key order) always produce the same key.
export const buildCacheKey = (reportName, params = {}) => {
    const sortedEntries = Object.entries(params).sort(([a], [b]) => a.localeCompare(b));
    return `${reportName}::${JSON.stringify(sortedEntries)}`;
};

// Returns the cached value if present and not yet expired, otherwise null.
// Never throws — any IndexedDB error is treated as a cache miss, so a
// caching problem can never block the actual report from loading.
// Returns { data, savedAt } if present and not yet expired, otherwise null.
export const getCachedReport = async (key) => {
    try {
        const db = await openDb();
        return await new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, "readonly");
            const req = tx.objectStore(STORE_NAME).get(key);
            req.onsuccess = () => {
                const entry = req.result;
                if (!entry) return resolve(null);
                if (Date.now() - entry.savedAt > TTL_MS) return resolve(null); // expired
                resolve({ data: entry.data, savedAt: entry.savedAt });
            };
            req.onerror = () => resolve(null);
        });
    } catch {
        return null;
    }
};

export const REPORT_CACHE_TTL_MS = TTL_MS;

export const setCachedReport = async (key, data) => {
    try {
        const db = await openDb();
        await new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, "readwrite");
            tx.objectStore(STORE_NAME).put({ key, data, savedAt: Date.now() });
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve(); // best-effort — a failed cache write shouldn't break anything
        });
    } catch {
        /* best-effort, ignore */
    }
};

// Explicit cache-buster for "Refresh" buttons — deletes just this one key
// rather than clearing everything, so other reports' caches stay intact.
export const invalidateCachedReport = async (key) => {
    try {
        const db = await openDb();
        await new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, "readwrite");
            tx.objectStore(STORE_NAME).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        });
    } catch {
        /* best-effort, ignore */
    }
};